import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "../../components";
import * as ROUTES from "../../constants/routes";
import { pluralize } from "@sellar-io/common";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { VolumeDiscountCreateDialog } from "./components/VolumeDiscountCreateDialog";
import { useVolumeDiscountsList } from "../../hooks/api/v2/volumeDiscounts/useVolumeDiscountsList";
import { useVolumeDiscountsDelete } from "../../hooks/api/v2/volumeDiscounts/useVolumeDiscountsDelete";
import { VolumeDiscountsListResponseType } from "@sellar-io/api-v2/types";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  H5,
  H6,
  ListItemIconTile,
  SingleFieldDialog,
  BadgeZinc,
  BadgeGreen,
  SingleFieldDialogHeader,
  SingleFieldDialogContent,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { useIsSellarAdmin } from "../../hooks/useIsSellarAdmin";
import { Alert } from "~/components/alert/Alert";
import { useSupplier } from "~/hooks/api/v2/supplier/useSupplier";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useVolumeDiscountsMigrate } from "~/hooks/api/v2/volumeDiscounts/useVolumeDiscountsMigrate";
import { useVolumeDiscountMode } from "../../hooks/useVolumeDiscountMode";
import {
  ConditionalVolumeDiscountsPrompt,
  RevertToBasicVolumeDiscountDialog,
  VolumeDiscountAdvancedSetupDialog,
} from "./components/VolumeDiscountModeDialogs";

export const VolumeDiscountsList = () => {
  const navigate = useNavigate();
  const [createNewActive, setCreateNewActive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
    priceLists: string[];
  } | null>(null);
  const [advancedSetupDialogActive, setAdvancedSetupDialogActive] =
    useState(false);
  const [revertDialogActive, setRevertDialogActive] = useState(false);

  const isAdmin = useIsSellarAdmin();
  const volumeDiscountMode = useVolumeDiscountMode();

  const deleteVolumeDiscountMutation = useVolumeDiscountsDelete(
    deleteTarget?.id ?? 0,
  );

  const volumeDiscountsListQuery = useVolumeDiscountsList({
    sortBy: "name",
    sortDir: "asc",
    limit: 10,
  });

  const flatLists = useMemo(
    () => volumeDiscountsListQuery.data?.pages.flat() ?? [],
    [volumeDiscountsListQuery.data],
  );

  if (volumeDiscountMode.isLoading) return null;

  const getSubtitle = (
    volumeDiscount: VolumeDiscountsListResponseType[number],
  ) => {
    const nbDiscounts = volumeDiscount.PriceTierDiscounts.length;
    if (nbDiscounts === 0) {
      return "No volume discounts";
    }
    return `${nbDiscounts} volume ${pluralize("discount", nbDiscounts)}`;
  };

  return (
    <div className="flex flex-col gap-4">
      {isAdmin && <MigrateVolumeDiscounts />}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <H5>Volume discount sets</H5>
          <span className="text-zinc-500">
            Create reusable sets of volume discounts to apply to your price
            lists
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DataButton
            className="flex items-center gap-1"
            border={true}
            onClick={() => setCreateNewActive(true)}
          >
            <Icon icon="PLUS" />
            New
          </DataButton>
          {volumeDiscountMode.modeAvailable &&
            volumeDiscountMode.isAdvancedMode && (
              <Dropdown>
                <DropdownButton>
                  <DataButton className="py-2.5" border={true}>
                    <Icon
                      icon="DOTS_VERTICAL"
                      className="text-black"
                      size={0.7}
                    />
                  </DataButton>
                </DropdownButton>
                <DropdownItems>
                  <DropdownItem
                    className="text-sm text-red-700"
                    onClick={() => setRevertDialogActive(true)}
                  >
                    Revert back to basic mode
                  </DropdownItem>
                </DropdownItems>
              </Dropdown>
            )}
        </div>
      </div>
      <InfiniteList
        query={volumeDiscountsListQuery}
        className="mb-8 flex flex-col gap-2"
      >
        {flatLists.map((volumeDiscount) => (
          <ListItem
            key={volumeDiscount.id}
            name={volumeDiscount.name}
            subtitle={getSubtitle(volumeDiscount)}
            numberOfPriceLists={volumeDiscount.numOfPriceLists}
            onClick={() =>
              navigate(
                ROUTES.VOLUME_DISCOUNT.href({
                  volumeDiscountId: volumeDiscount.id,
                }),
              )
            }
            rightComponent={
              <Dropdown>
                <DropdownButton>
                  <Button size={"small"} color={"transparent"}>
                    <Icon icon={"PENCIL"} className="text-black" size={0.7} />
                  </Button>
                </DropdownButton>
                <DropdownItems>
                  <DropdownItem
                    className="border-b border-zinc-200 text-sm"
                    onClick={() =>
                      navigate(
                        ROUTES.VOLUME_DISCOUNT.href({
                          volumeDiscountId: volumeDiscount.id,
                        }),
                      )
                    }
                  >
                    Edit
                  </DropdownItem>
                  <DropdownItem
                    className="border-b border-zinc-200 text-sm text-red-700"
                    onClick={() =>
                      setDeleteTarget({
                        id: volumeDiscount.id,
                        name: volumeDiscount.name,
                        priceLists: volumeDiscount.PriceListNames,
                      })
                    }
                  >
                    Delete
                  </DropdownItem>
                </DropdownItems>
              </Dropdown>
            }
          />
        ))}
      </InfiniteList>
      {volumeDiscountMode.isBasicMode && (
        <ConditionalVolumeDiscountsPrompt
          title="Boost order size by setting different volume discounts for different delivery methods"
          message="Set up conditional volume discounts to make your discounts dependent on your customer's chosen delivery method"
          btnText="Learn more"
          onClick={() => setAdvancedSetupDialogActive(true)}
        />
      )}
      <VolumeDiscountCreateDialog
        active={createNewActive}
        setActive={setCreateNewActive}
      />
      <SingleFieldDialog
        active={!!deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
        size="large"
      >
        <SingleFieldDialogHeader
          title={`Are you sure you want to delete this '${deleteTarget?.name}' volume discount set?`}
          description="This volume discount set is applied to the price lists shown below. Deleting this volume discount set will remove it from the price lists"
          icon="INFORMATION"
          iconClassName="bg-red-50 text-red-600"
        />
        <SingleFieldDialogContent>
          <Card className="flex flex-col gap-2 bg-white bg-zinc-100 p-4">
            <H5>Applied price list(s):</H5>
            <ul className="list-disc pl-5">
              {deleteTarget?.priceLists.map((priceList) => (
                <li key={priceList}>{priceList}</li>
              ))}
            </ul>
            {deleteTarget?.priceLists.length === 0 && <i>No price lists</i>}
          </Card>
        </SingleFieldDialogContent>
        <SingleFieldDialogCTA>
          <Button color="transparent" onClick={() => setDeleteTarget(null)}>
            Dismiss
          </Button>
          <Button
            color="red"
            onClick={() =>
              deleteVolumeDiscountMutation.mutate(undefined, {
                onSuccess: () => setDeleteTarget(null),
              })
            }
            loading={deleteVolumeDiscountMutation.isPending}
          >
            Delete
          </Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
      <VolumeDiscountAdvancedSetupDialog
        active={advancedSetupDialogActive}
        onDismiss={() => setAdvancedSetupDialogActive(false)}
      />
      <RevertToBasicVolumeDiscountDialog
        active={revertDialogActive}
        onDismiss={() => setRevertDialogActive(false)}
      />
    </div>
  );
};

const ListItem = ({
  name,
  subtitle,
  numberOfPriceLists,
  onClick,
  rightComponent,
}: {
  name: string;
  subtitle: string;
  numberOfPriceLists: number;
  onClick: () => void;
  rightComponent?: ReactNode;
}) => {
  return (
    <Card
      onClick={onClick}
      className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon="SALE" />
      <div className="flex w-full max-w-[calc(100%-63px)] items-center gap-4 md:flex-nowrap">
        <div className="flex flex-col overflow-x-hidden">
          <div className="font-semibold">{name}</div>
          <div className="w-full overflow-x-hidden">
            <p className="w-full overflow-x-hidden text-sm text-nowrap text-ellipsis text-zinc-700">
              {subtitle}
            </p>
          </div>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          {numberOfPriceLists === 0 ? (
            <BadgeZinc icon="TAG_OUTLINE">0</BadgeZinc>
          ) : (
            <BadgeGreen icon="TAG_OUTLINE">{numberOfPriceLists}</BadgeGreen>
          )}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            {rightComponent ?? <Icon icon="CHEVRON_RIGHT" size={1} />}
          </div>
        </span>
      </div>
    </Card>
  );
};

const MigrateVolumeDiscounts = () => {
  const [checked, setChecked] = useState(false);
  const migrateVolumeDiscounts = useVolumeDiscountsMigrate();

  const { business } = useSellarCurrentBusiness();

  const { data: supplier } = useSupplier(business?.Supplier?.id);

  if (!supplier || supplier.hasMigratedVolumeDiscounts) return <></>;

  return (
    <Card className="border border-red-500 bg-white">
      <CardHeader className="flex flex-col bg-white p-4">
        <H5>Migrate this suppliers volume discounts</H5>
        <H6>This can only be run once</H6>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4">
        <Alert
          type="information"
          title="Confirmation"
          message="This will migrate the volume discounts to the new delivery methods system. This can only be done once."
          className="w-full"
        >
          <div className="flex flex-col gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-300 bg-blue-200 p-2 hover:bg-blue-300">
              <input
                id="checkbox"
                type="checkbox"
                onChange={(e) => setChecked(e.target.checked)}
              />
              Check to confirm you understand -{" "}
              <b>this message won't show after the migration</b>
            </label>
          </div>
        </Alert>
        <Button
          disabled={!checked}
          loading={migrateVolumeDiscounts.isPending}
          onClick={() => migrateVolumeDiscounts.mutate()}
          className="w-full"
          color="red"
        >
          Migrate
        </Button>
      </CardContent>
    </Card>
  );
};
